import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { Alert, PageTitle } from 'components';
import { appConfig } from 'config/app';
import { DEFAULT_BACKDATE_LIMIT } from 'config/const';
import validations from 'lib/Validations';
import { SubmitPaymentModal } from 'modals';
import routeConstants, { URL } from 'routes/routeConstants';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { EmployeePayrollTips, getPayPeriodPayrollTipsRequest, selectEmployeePayrollTips, selectEmployees } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { Payment, payPaymentSetRequest } from 'store/Payment';
import { selectSite, selectSiteAccountBalance } from 'store/Site';
import { IStore } from 'store/types';
import { IEmployee, IPayee, IPayPeriod, ISubmitPaymentPayload, IUploadPayment, PaymentWorkflowTypes } from 'types';
import { buildPaymentWithholdings, calculateOverdraftStatus, calculatePayeesTotals, calculatePaymentTotals, centsToDollarsDisplay, createPayeeFromEmployee, createPaymentPayload, siteUsesHoldbacks, siteUsesWithholdings } from 'utils';
import { MakePaymentStep } from '../types';
import { PaymentInput, PaymentReview, SelectDate, SelectPayees, UploadPayment } from './steps';

interface IParams {
  workflow: PaymentWorkflowTypes;
  step: MakePaymentStep;
}

const StandardUpload = () => {
  const { workflow = PaymentWorkflowTypes.STANDARD, step = MakePaymentStep.SelectPayees } = useParams<IParams>();
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentEmployee = useSelector(selectCurrentEmployee());
  const siteBalance = useSelector(selectSiteAccountBalance());
  const { data: employees } = useSelector(selectEmployees()) || { data: [], total: null };
  const payrollTips = useSelector(selectEmployeePayrollTips());
  const isPayrollTipsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeePayrollTips.GET_PAY_PERIOD_PAYROLL_TIPS_REQUEST));
  const isCreatePaymentLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.CREATE_PAYMENT_REQUEST));
  const siteWithSettings = useSelector(selectSite());
  const {
    perTipMax = appConfig.TIP.MAX,
    strictTipPool = false,
    holdback: siteHoldbacks,
    createPayments: createPaymentsEnabled,
    rolePolicy: siteRolePolicy,
    timezone,
    overdraftLimit = 0,
    backdateLimit = DEFAULT_BACKDATE_LIMIT,
  } = siteWithSettings?.settings || {};
  const currentSiteId = currentEmployee?.siteId || '';

  const [cashEnabled, setCashEnabled] = useState(false);
  const [tipPoolLimit, setTipPoolLimit] = useState<number>();
  const [currentPayPeriod, setCurrentPayPeriod] = useState<IPayPeriod>();
  const [paymentUploads, setPaymentUploads] = useState<IUploadPayment[]>([]);
  const isUploadingPayment = paymentUploads.length > 0;

  const currentSiteUsesHoldbacks = siteUsesHoldbacks(siteHoldbacks);
  const resetMakePayment = () => browserHistory.push(routeConstants.private.makePayment);
  const handleStepChange = (newStep: MakePaymentStep) => browserHistory.push(`${routeConstants.private.makePayment}/${workflow}/${newStep}`);
  const goToPayeesStep = () => handleStepChange(workflow === PaymentWorkflowTypes.STANDARD ? MakePaymentStep.SelectPayees : MakePaymentStep.FileUpload);

  // reset to first step on page reload
  useEffect(() => {
    if (!siteRolePolicy || step !== MakePaymentStep.SelectDate) resetMakePayment();
  }, [siteRolePolicy]);

  const formik = useFormik({
    initialValues: {
      payees: [],
      description: '',
      authorizedAgreement: false,
      paymentDate: '',
    },
    onSubmit: (values: ISubmitPaymentPayload) => {
      const withholdings = buildPaymentWithholdings(values.payees, siteWithSettings, currentPayPeriod, payrollTips?.data);
      dispatch(payPaymentSetRequest(createPaymentPayload(values, currentSiteId, workflow, withholdings), hideSubmitPaymentModal));
    },
    validationSchema: validations.makePaymentValidationSchema(perTipMax, timezone, backdateLimit),
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const { payees } = formik.values;
  const { values, setFieldValue, errors, isValid, handleSubmit } = formik;

  const onDateSelect = (date: string, payPeriod?: IPayPeriod) => {
    setFieldValue('paymentDate', date);

    if (siteUsesWithholdings(siteWithSettings)) {
      const onSuccess = (period: IPayPeriod) => {
        setCurrentPayPeriod(period);
        goToPayeesStep();
      };
      dispatch(getPayPeriodPayrollTipsRequest(currentSite.id, date, payPeriod, onSuccess));
    } else {
      goToPayeesStep();
    }
  };

  const goToReviewStep = () => {
    const reviewPayees = calculatePayeesTotals(payees, siteWithSettings, payrollTips?.data);
    setFieldValue('payees', reviewPayees);
    handleStepChange(MakePaymentStep.PaymentReview);
  };

  const goBackToInputStep = () => {
    const payeesWithoutTotals = payees.map((p) => ({ ...p, holdbackAmount: 0, withholdingAmount: 0, net: p.gross }));
    setFieldValue('payees', payeesWithoutTotals);
    handleStepChange(MakePaymentStep.PaymentInput);
  };

  const handleCash = (enabled: boolean) => {
    setCashEnabled(enabled);

    if (!enabled) {
      const payeesWithoutCash = payees.map((p) => ({ ...p, cash: 0 }));
      setFieldValue('payees', payeesWithoutCash);
    }
  };

  const totals = useMemo(() => calculatePaymentTotals(payees), [payees]);

  const [showSubmitPaymentModal, hideSubmitPaymentModal] = useModal(
    () => <SubmitPaymentModal values={values} totals={totals} setFieldValue={setFieldValue} disabled={!isValid} isLoading={isCreatePaymentLoading} onClose={hideSubmitPaymentModal} onSubmit={handleSubmit} />,
    [values, totals, setFieldValue, isValid, isCreatePaymentLoading, handleSubmit]
  );

  const netInstant = totals.instant - totals.holdbacks;
  const overdraftWarning = calculateOverdraftStatus(netInstant, overdraftLimit, siteBalance);

  const pageHeaderContent = currentSite && <PageTitle data-cy="account-balance" title={currentSite.name} subtitle={`Account Balance: ${siteBalance || siteBalance === 0 ? centsToDollarsDisplay(siteBalance) : '--'}`} />;

  if (!siteRolePolicy) return null;

  return (
    <FormikProvider value={formik}>
      <FieldArray
        name="payees"
        render={(arrayHelpers) => {
          const setPayees = (newPayees: IPayee[], replace?: boolean) => {
            formik.setFieldValue('payees', [...(replace ? [] : payees), ...newPayees]);
          };

          const onAddPayees = (employees: IEmployee[]) => {
            const newPayees = employees.reduce((acc: IPayee[], employee) => {
              if (!payees.some((p) => p.id === employee.id)) {
                acc.push(createPayeeFromEmployee(employee, currentSiteId));
              }
              return acc;
            }, []);

            setPayees(newPayees);
          };

          const onRemovePayee = (index: number) => arrayHelpers.remove(index);

          const onRemoveAllPayees = () => {
            formik.setFieldValue('payees', []);
            formik.setFieldTouched('payees', false);
          };

          return (
            <>
              <NavigationPrompt
                when={(_, nextLocation) => {
                  // when redirected after a successful submission
                  if (nextLocation?.pathname === URL.MakePaymentSummary) return false;

                  // when redirected after logout or changing organization
                  if (nextLocation?.pathname === URL.Login || nextLocation?.pathname === URL.RootPrivate) return false;

                  return (!nextLocation || !nextLocation.pathname.includes(workflow)) && (payees.length > 0 || isUploadingPayment);
                }}
              >
                {({ onConfirm, onCancel }) => (
                  <Alert
                    dataCy="leave-payment-alert"
                    title="Leave make payment?"
                    message="Leaving this page will abandon all work. Are you sure you want to leave?"
                    onLeftAction={onCancel}
                    onRightAction={onConfirm}
                    leftActionLabel="Cancel"
                    rightActionLabel="Leave"
                    isActive
                  />
                )}
              </NavigationPrompt>

              {step === MakePaymentStep.SelectDate && (
                <SelectDate
                  siteId={currentSiteId}
                  onBack={resetMakePayment}
                  onNext={onDateSelect}
                  businessDate={values.paymentDate}
                  siteTimezone={timezone}
                  backdateLimit={backdateLimit}
                  isLoading={isPayrollTipsLoading}
                  pageHeaderContent={pageHeaderContent}
                />
              )}

              {step === MakePaymentStep.SelectPayees && (
                <SelectPayees
                  onBack={() => handleStepChange(MakePaymentStep.SelectDate)}
                  onNext={() => handleStepChange(MakePaymentStep.PaymentInput)}
                  payees={payees}
                  employees={employees}
                  currentEmployee={currentEmployee}
                  pageHeaderContent={pageHeaderContent}
                  onAddPayees={onAddPayees}
                  onRemovePayee={onRemovePayee}
                  rolePolicy={siteRolePolicy}
                />
              )}

              {step === MakePaymentStep.FileUpload && (
                <UploadPayment
                  onBack={() => handleStepChange(MakePaymentStep.SelectDate)}
                  onNext={() => handleStepChange(MakePaymentStep.PaymentInput)}
                  paymentUploads={paymentUploads}
                  setPaymentUploads={setPaymentUploads}
                  currentEmployee={currentEmployee}
                  currentSiteId={currentSiteId}
                  pageHeaderContent={pageHeaderContent}
                  setPayees={setPayees}
                  onRemoveAllPayees={onRemoveAllPayees}
                  rolePolicy={siteRolePolicy}
                />
              )}

              {step === MakePaymentStep.PaymentInput && (
                <PaymentInput
                  onBack={() => (isUploadingPayment ? handleStepChange(MakePaymentStep.FileUpload) : handleStepChange(MakePaymentStep.SelectPayees))}
                  onNext={goToReviewStep}
                  businessDate={values.paymentDate}
                  onAddPayees={onAddPayees}
                  onRemovePayee={onRemovePayee}
                  currentSiteId={currentSiteId}
                  currentEmployee={currentEmployee}
                  pageHeaderContent={pageHeaderContent}
                  payees={payees}
                  isPaymentUpload={isUploadingPayment}
                  totals={totals}
                  cashEnabled={cashEnabled}
                  setCashEnabled={handleCash}
                  tipPoolLimit={tipPoolLimit}
                  setTipPoolLimit={setTipPoolLimit}
                  employeeTipLimit={perTipMax}
                  rolePolicy={siteRolePolicy}
                  formik={formik}
                  showOverdraftWarning={overdraftWarning.showOverdraft}
                  overdraftText={overdraftWarning.message || ''}
                  overdraftContinue={overdraftWarning.overdraftContinue}
                  strictTipPool={strictTipPool}
                />
              )}

              {step === MakePaymentStep.PaymentReview && (
                <PaymentReview
                  onBack={goBackToInputStep}
                  onNext={showSubmitPaymentModal}
                  businessDate={values.paymentDate}
                  setFieldValue={setFieldValue}
                  createPaymentsEnabled={createPaymentsEnabled}
                  currentSite={currentSite}
                  pageHeaderContent={pageHeaderContent}
                  totals={totals}
                  cashEnabled={cashEnabled}
                  payees={payees}
                  rolePolicy={siteRolePolicy}
                  siteUsesHoldbacks={currentSiteUsesHoldbacks}
                  showOverdraftWarning={overdraftWarning.showOverdraft}
                  overdraftText={overdraftWarning.message || ''}
                  overdraftContinue={overdraftWarning.overdraftContinue}
                  errors={errors}
                />
              )}
            </>
          );
        }}
      />
    </FormikProvider>
  );
};

export default StandardUpload;
